// src/components/forms/SurveyListActiveItem.js
import React, { useState } from 'react';
import { Eye, Archive, MoreVertical, UserCog, BarChart3, MessageSquare, Layers2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import { toast } from "sonner"
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi'; // Adjust path if needed
import { useAuth } from '../../hooks/useAuth'; // Adjust path if needed


import API_CONFIG from '../../config/api'
const BASE_URL = API_CONFIG.BASE_URL;

// Expects surveyInfo (matching SurveyInfo Pydantic model) and refreshData function
function SurveyListActiveItem({ surveyInfo, refreshData }) {
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const surveyTitleDisplay = surveyInfo?.title || 'Untitled Survey';
	const navigate = useNavigate();
	
	
	const { authenticatedFetch } = useAuthenticatedApi();
	const { isAuthenticated } = useAuth(); // Assuming useAuth provides isAuthenticated or user object

	// TODO you will have to implement an inbetween state where the survey is "paused" which means you cannot collect data but it not archived yet
	// const handleDeactivateClick = () => {
	// 	const confirmed = window.confirm("Are you sure you want to deactivate this survey? This will stop collecting responses.\nNot implemented yet");
	// 	if (confirmed) {
	// 		console.log(`Attempting to deactivate survey with ID: ${surveyInfo.survey_id}`);
	// 	} else {
	// 		console.log("Deactivate action cancelled.");
	// 	}
	// };

	const handlePreviewClick = () => {
		console.log(`Navigating to preview survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/private/evaa-survey/preview/${surveyInfo.survey_id}`);
	};

	const handleAudienceConfigClick = () => {
		console.log(`Navigating to audience config for survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/dashboard/audience/${surveyInfo.survey_id}`);
	};

	const handleResponseClick = () => {
		console.log(`Navigating to responses for survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/dashboard/responses/${surveyInfo.survey_id}`);
	};

	const handleAnalyticsClick = () => {
		console.log(`Navigating to analytics for survey with ID: ${surveyInfo.survey_id}`);
		alert(`Analystics Not implemetented yet`);
	};

	const handleDuplicateClick = async () => {
		try {
			const url = BASE_URL + `/surveys/duplicate/${surveyInfo.survey_id}`
			const result = await authenticatedFetch(url, {}, 'POST');
			if (!result.ok) {
				// Handle other non-2xx status codes (e.g., 500, 401, 403)
				let errorMessage = `Failed to duplicate survey to draft ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Ignore if response body isn't JSON
				}
				console.error("SurveyListActiveItem API Error in handleDuplicateClick:", errorMessage, result);
				toast(errorMessage);
				return; // Stop further execution
			}
			const data = await result.json()
			if (data && data.title && data.survey_id) {
				toast(`✅​ survey successfully duplicated`);

			} else {
				console.error("Failed to duplicate survey:", result);
				toast("Unexpected API response", data);
			}

		} catch (err) {
			const errMessage = "​⁉️​ Error while duplicating the survey: " + err
			console.error(errMessage);
			toast(errMessage);
		} finally {
			refreshData(); // <--- THIS IS THE KEY STEP!
			closeDropdown();
		}
	};

	const handleArchiveClick = async () => {
		const confirmed = window.confirm("🚨​ You are about to archive your survey, all in progress task will be deactivated, no coming back is implemented for the moment");
		if (!confirmed) {
			toast("Archival cancelled.")
			console.log("Archival cancelled.");
			return;
		}
		if (!isAuthenticated) { 
			return;
		}

		try {
			const url = BASE_URL + `/active_surveys/archive-survey/${surveyInfo.survey_id}?new_status=archived`
			const result = await authenticatedFetch(url, {}, 'PUT');
			if (!result.ok) {
				// Handle other non-2xx status codes (e.g., 500, 401, 403)
				let errorMessage = `Failed to update the status of the survey to archived ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Ignore if response body isn't JSON
				}
				console.error("SurveyListActiveItem API Error:", errorMessage, result);
				toast(errorMessage);
				return; // Stop further execution
			}
			const data = await result.json()
			if (data && data.message && data.survey_id) {
				toast(`✅​ ${data.survey_id} archived successfully`);

			} else {
				console.error("Failed to update status:", result);
				toast("Unexpected API response", data);
			}

		} catch (err) {
			const errMessage = "​⁉️​ Error updating status: " + err
			console.error(errMessage);
			toast(errMessage);
		} finally {
			refreshData(); // <--- THIS IS THE KEY STEP!
			closeDropdown();
		}
	};

	const toggleDropdown = () => {
		setDropdownOpen(!dropdownOpen);
	};

	const closeDropdown = () => {
		setDropdownOpen(false);
	};

	return (
		<div className='border shadow-sm rounded-lg p-4 flex flex-col h-full relative'>
			<div className='flex justify-between items-center'>
				<h2 className='text-lg font-semibold text-black flex-grow truncate'>{surveyTitleDisplay}</h2>
				<div className="flex items-center gap-1">
					<span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
				</div>
			</div>

			<h3 className='text-sm text-gray-500 mt-1 mb-4'>
				ID: {surveyInfo.survey_id} - Created: { new Date(surveyInfo.created_at).toLocaleDateString() }
			</h3>

			<hr className='my-2'></hr>

			{/* Action Buttons */}
			<div className='flex justify-between items-center mt-auto'>
				{/* Primary Actions - Most Important */}
				<div className='flex gap-2'>
					<Button
						onClick={handleResponseClick}
						variant="primary"
						className="flex gap-2 items-center px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
					>
						<MessageSquare className='h-4 w-4'/> Responses
					</Button>

					<Button
						onClick={handlePreviewClick}
						variant="secondary"
						className="flex gap-2 items-center px-3 py-1.5 border rounded text-sm text-gray-700 hover:bg-gray-100 transition-colors"
					>
						<Eye className='h-4 w-4'/> Preview
					</Button>
				</div>

				{/* More Actions Dropdown */}
				<div className='relative'>
					<button
						onClick={toggleDropdown}
						className="flex items-center justify-center w-8 h-8 border rounded hover:bg-gray-100 transition-colors"
					>
						<MoreVertical className='h-4 w-4'/>
					</button>

					{dropdownOpen && (
						<>
							{/* Overlay to close dropdown when clicking outside */}
							<div
								className="fixed inset-0 z-10"
								onClick={closeDropdown}
							></div>
							
							{/* Dropdown Menu */}
							<div className="absolute right-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-20">
								<div className="py-1">
	
									{/*  // TODO you will have to implement an inbetween state where the survey is "paused" which means you cannot collect data but it not archived yet
									<button
										onClick={() => { handleDeactivateClick(); closeDropdown(); }}
										className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
									>
										<Pause className='h-4 w-4'/> Deactivate
									</button> */}
									<button
										onClick={() => { handleAudienceConfigClick(); closeDropdown(); }}
										className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
									>
										<UserCog className='h-4 w-4'/> Audience Config
									</button>
									<button
										onClick={() => { handleAnalyticsClick(); closeDropdown(); }}
										className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
									>
										<BarChart3 className='h-4 w-4'/> Analytics
									</button>
									<hr className="my-1"/>
									<button
										onClick={() => { handleDuplicateClick(); closeDropdown(); }}
										className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-red-50 flex items-center gap-2"
									>
										<Layers2 className='h-4 w-4'/> Duplicate to draft
									</button>
									<button
										onClick={() => { handleArchiveClick(); closeDropdown(); }}
										className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
									>
										<Archive className='h-4 w-4'/> Archive
									</button>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default SurveyListActiveItem;